import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaCheck,
  FaTimes,
  FaPlus,
  FaCalendarAlt,
  FaClock,
  FaExclamationTriangle,
  FaFolder,
} from 'react-icons/fa';

export default function BlogTable({ blogs, onDelete, onPublish, onUnpublish }) {
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, blog: null });

  const confirmDelete = (blog) => {
    setDeleteModal({ isOpen: true, blog });
  };

  const handleExecuteDelete = () => {
    if (deleteModal.blog) {
      onDelete(deleteModal.blog._id);
      setDeleteModal({ isOpen: false, blog: null });
    }
  };

  if (!blogs || blogs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-500 shadow-inner">
          <FaFolder className="text-3xl" />
        </div>
        <h3 className="mt-4 text-lg font-bold text-slate-800">No blog posts found</h3>
        <p className="mt-1 max-w-sm text-sm text-slate-500">
          No articles match your current search or filter criteria. Create a new article or adjust filters.
        </p>
        <Link
          to="/admin/blogs/create"
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
        >
          <FaPlus /> Create First Blog Post
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-4">Article</th>
                <th className="px-5 py-4">Category & Tags</th>
                <th className="px-5 py-4">Author</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {blogs.map((blog) => {
                const isPublished = blog.status === 'Published';
                return (
                  <tr key={blog._id} className="group transition hover:bg-slate-50/60">
                    {/* 1. Article Column */}
                    <td className="px-5 py-4">
                      <div className="flex items-start gap-4">
                        <div className="h-14 w-20 flex-shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
                          {blog.featuredImage ? (
                            <img
                              src={blog.featuredImage}
                              alt={blog.title}
                              className="h-full w-full object-cover transition duration-200 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-slate-400">
                              No image
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 max-w-md">
                          <Link
                            to={`/admin/blogs/edit/${blog._id}`}
                            className="font-semibold text-slate-900 hover:text-amber-600 transition line-clamp-1"
                          >
                            {blog.title}
                          </Link>
                          <p className="mt-0.5 text-xs text-slate-500 line-clamp-1">
                            {blog.shortDescription || 'No description provided.'}
                          </p>
                          <p className="mt-1 font-mono text-[11px] text-slate-400 truncate">
                            slug: /{blog.slug}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* 2. Category & Tags */}
                    <td className="px-5 py-4 align-top">
                      <div className="space-y-1.5">
                        <span className="inline-block rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 border border-amber-200/50">
                          {blog.category || 'General'}
                        </span>
                        {blog.tags && blog.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {blog.tags.slice(0, 2).map((t) => (
                              <span key={t} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                                #{t}
                              </span>
                            ))}
                            {blog.tags.length > 2 && (
                              <span className="text-[10px] text-slate-400">+{blog.tags.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* 3. Author */}
                    <td className="px-5 py-4 align-top text-slate-700">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white uppercase">
                          {(blog.authorName || 'A').slice(0, 2)}
                        </div>
                        <span className="text-xs font-medium text-slate-800">{blog.authorName || 'Admin'}</span>
                      </div>
                    </td>

                    {/* 4. Status */}
                    <td className="px-5 py-4 align-top">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          isPublished
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${isPublished ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        {blog.status}
                      </span>
                    </td>

                    {/* 5. Date & Reading Time */}
                    <td className="px-5 py-4 align-top text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <FaCalendarAlt className="text-[10px] text-slate-400" />
                        <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                        <FaClock className="text-[10px]" />
                        <span>{blog.readingTime || 1} min read</span>
                      </div>
                    </td>

                    {/* 6. Action Buttons */}
                    <td className="px-5 py-4 align-top text-right">
                      <div className="inline-flex items-center gap-1 rounded-xl bg-slate-50 p-1 border border-slate-200">
                        {/* View Preview */}
                        <Link
                          to={`/admin/blogs/preview/${blog.slug}`}
                          title="Preview Blog"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition hover:bg-white hover:text-slate-900 hover:shadow-sm"
                        >
                          <FaEye className="text-xs" />
                        </Link>

                        {/* Edit */}
                        <Link
                          to={`/admin/blogs/edit/${blog._id}`}
                          title="Edit Blog"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition hover:bg-white hover:text-amber-600 hover:shadow-sm"
                        >
                          <FaEdit className="text-xs" />
                        </Link>

                        {/* Publish / Unpublish Toggle */}
                        {isPublished ? (
                          <button
                            type="button"
                            onClick={() => onUnpublish(blog._id)}
                            title="Unpublish (Convert to Draft)"
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-amber-600 transition hover:bg-amber-100/50 hover:shadow-sm"
                          >
                            <FaTimes className="text-xs" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onPublish(blog._id)}
                            title="Publish immediately"
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-emerald-600 transition hover:bg-emerald-100/50 hover:shadow-sm"
                          >
                            <FaCheck className="text-xs" />
                          </button>
                        )}

                        {/* Delete */}
                        <button
                          type="button"
                          onClick={() => confirmDelete(blog)}
                          title="Delete Blog"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-red-500 transition hover:bg-red-50 hover:text-red-700 hover:shadow-sm"
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl animate-in fade-in duration-200">
            <div className="flex items-center gap-3 text-red-600">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <FaExclamationTriangle className="text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Delete Blog Post</h3>
                <p className="text-xs text-slate-500">This action cannot be undone.</p>
              </div>
            </div>

            <p className="mt-4 text-sm text-slate-600">
              Are you sure you want to permanently delete{' '}
              <strong className="text-slate-900">&ldquo;{deleteModal.blog?.title}&rdquo;</strong>?
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, blog: null })}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteDelete}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
              >
                Yes, Delete Post
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
